import React from 'react';
import { Box, FormControl, InputLabel, MenuItem, Select, TextField, Typography } from '@mui/material';

export interface FilterDef {
  key: string;
  label: string;
  type: 'select' | 'text';
  options?: { value: string; label: string }[];
  placeholder?: string;
}

interface FilterPanelProps {
  filters: FilterDef[];
  values: Record<string, string>;
  onChange: (key: string, value: string) => void;
  onClear: () => void;
  /** Extra controls rendered inline (e.g. DateRangeSelector) */
  children?: React.ReactNode;
}

export const FilterPanel: React.FC<FilterPanelProps> = ({ filters, values, onChange, onClear, children }) => {
  const hasActiveFilters = Object.values(values).some(v => v !== '' && v != null);

  return (
    <Box
      sx={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: 1.5,
        p: 2,
        bgcolor: '#fff',
        borderRadius: '8px',
        border: '1px solid',
        borderColor: 'divider',
        alignItems: 'flex-end',
      }}
    >
      {filters.map(filter => (
        <Box key={filter.key} sx={{ minWidth: 160 }}>
          {filter.type === 'select' ? (
            <FormControl size="small" fullWidth>
              <InputLabel
                sx={{ fontSize: '0.625rem', textTransform: 'uppercase', fontWeight: 600, letterSpacing: '0.04em' }}
              >
                {filter.label}
              </InputLabel>
              <Select
                value={values[filter.key] ?? ''}
                onChange={e => onChange(filter.key, e.target.value)}
                label={filter.label}
                sx={{ bgcolor: 'grey.50', fontSize: '0.8125rem' }}
              >
                <MenuItem value="">All</MenuItem>
                {filter.options?.map(opt => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          ) : (
            <TextField
              size="small"
              label={filter.label}
              placeholder={filter.placeholder ?? `Search ${filter.label.toLowerCase()}...`}
              value={values[filter.key] ?? ''}
              onChange={e => onChange(filter.key, e.target.value)}
              sx={{ bgcolor: 'grey.50' }}
             slotProps={{ inputLabel: {
                sx: { fontSize: '0.625rem', textTransform: 'uppercase', fontWeight: 600 },
              }, htmlInput: { sx: { fontSize: '0.8125rem' } } }}/>
          )}
        </Box>
      ))}
      {children}
      {hasActiveFilters && (
        <Typography
          onClick={onClear}
          sx={{
            fontSize: '0.6875rem',
            color: 'text.secondary',
            cursor: 'pointer',
            alignSelf: 'center',
            '&:hover': { color: 'error.main' },
          }}
        >
          Clear all
        </Typography>
      )}
    </Box>
  );
};
